import { useState, useEffect } from 'react'
import { EVENTS, trackEvent } from '../../../../../events'
import {
    ScoopButton,
    ScoopSpinner,
    ScoopTextField,
    ScoopText,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
    ScoopDivider,
    ScoopGrid,
    useToast,
} from '../../../../ui'
import { useModal, MODALS } from '../../../../modals'
import { cloneDeep, merge } from 'lodash'
import {
    usePutInboxData,
    useUpdateDatasetSemantics,
} from '../../../../../api/hooks/datasource/hook'
import {
    MagnifyingGlassIcon,
    SparkleIcon as SparklesIcon,
    CircleNotchIcon,
} from '@phosphor-icons/react'
import styles from './DatasetSemantics.module.scss'

export const DatasetSemantics = ({
    isLoadingGetInboxData,
    getInboxData,
    invalidateInboxData,
    inbox,
}) => {
    const { mutate: putInboxData, isPending: isPutInboxDataPending } = usePutInboxData()
    const { mutate: updateSemantics, isPending: isUpdateSemanticsPending } =
        useUpdateDatasetSemantics()

    const saveChangesModal = useModal(MODALS.COMMON.SAVE_CHANGES)
    const refreshOptionsModal = useModal(MODALS.SOURCE.REFRESH_OPTIONS)
    const toast = useToast()
    const [someInputHasChanged, setSomeInputHasChanged] = useState({})
    const [originalSemanticData, setOriginalSemanticData] = useState({})
    const [datasetSemanticsData, setDatasetSemanticsData] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        setIsLoading(true)
        //On load set the dataset semantics data and the original data
        if (!isLoadingGetInboxData && getInboxData) {
            const inboxDataTemp = cloneDeep(getInboxData)
            if (getInboxData.datasetSemantics) {
                setDatasetSemanticsData(cloneDeep(getInboxData.datasetSemantics || {}))
                setOriginalSemanticData(inboxDataTemp)
            } else {
                const datasetSemanticsDataTemp = handleCreateDataSemanticsWithInboxData()
                inboxDataTemp.datasetSemantics = datasetSemanticsDataTemp
                setDatasetSemanticsData(datasetSemanticsDataTemp)
                setOriginalSemanticData(inboxDataTemp)
            }
        }
        setIsLoading(false)
    }, [getInboxData, isLoadingGetInboxData])

    const handleCreateDataSemanticsWithInboxData = () => {
        const mappedCols = inbox?.tables[0]?.columns.map((col) => {
            return {
                columnName: col.columnName,
                columnInfo: {
                    columnType: col.columnType,
                },
            }
        })
        return {
            semantics: '',
            columnSemantics: mappedCols,
        }
    }

    const handleChangeInputs = (e, columnName) => {
        const val = e.target.value

        // Find the actual index in the original array
        const actualIndex = datasetSemanticsData.columnSemantics.findIndex(
            (col) => col.columnName === columnName
        )

        const originalValue =
            originalSemanticData.datasetSemantics.columnSemantics[actualIndex].semantics

        const copyOfUpdatedColumnSemantics = cloneDeep(datasetSemanticsData)

        //If the value is empty and the original value is undefined, delete the semantics
        if (val === '' && originalValue === undefined) {
            delete copyOfUpdatedColumnSemantics.columnSemantics[actualIndex].semantics
            setDatasetSemanticsData(copyOfUpdatedColumnSemantics)

            const someInputHasChangedTemp = cloneDeep(someInputHasChanged)
            someInputHasChangedTemp[columnName] = false
            setSomeInputHasChanged(someInputHasChangedTemp)
        } else {
            copyOfUpdatedColumnSemantics.columnSemantics[actualIndex].semantics = val
            setDatasetSemanticsData(copyOfUpdatedColumnSemantics)
            const someInputHasChangedTemp = cloneDeep(someInputHasChanged)
            if (val === originalValue) {
                someInputHasChangedTemp[columnName] = false
            } else {
                someInputHasChangedTemp[columnName] = true
            }
            setSomeInputHasChanged(someInputHasChangedTemp)
        }
    }

    const handleSubmit = async () => {
        trackEvent(EVENTS.DATASET.EDIT_SEMANTICS)
        const dataToSave = merge(originalSemanticData, { datasetSemantics: datasetSemanticsData })
        putInboxData({
            inboxName: inbox.inboxName,
            value: dataToSave,
            onSuccess: () => {
                invalidateInboxData()
                toast.success('Dataset semantics saved successfully')
            },
            onError: () => {
                toast.error('Failed to save dataset semantics')
            },
        })
    }

    const handleConfirmSave = async () => {
        await saveChangesModal.show({
            title: 'Save Changes',
            description: 'Do you want to save the changes to this dataset?',
            onSave: handleSubmit,
        })
    }

    const showRefreshOptionsDialog = () => {
        refreshOptionsModal.show({
            onRefreshAll: () => executeRefreshSemantics(true),
            onRefreshEmpty: () => executeRefreshSemantics(false),
        })
    }

    const handleConfirmRefresh = async () => {
        await saveChangesModal.show({
            title: 'Discard Unsaved Changes?',
            description:
                'You have unsaved changes. Refreshing with AI will discard your current edits. Do you want to continue?',
            confirmText: 'Yes, Continue',
            cancelText: 'Cancel',
            onSave: showRefreshOptionsDialog,
        })
    }

    const hasUnsavedChanges = () => {
        return Object.entries(someInputHasChanged).some(([key, value]) => value === true)
    }

    const handleRefreshSemanticsClick = () => {
        // Check if there are unsaved changes
        if (hasUnsavedChanges()) {
            handleConfirmRefresh()
        } else {
            // Show the refresh options dialog
            showRefreshOptionsDialog()
        }
    }

    const executeRefreshSemantics = (forceRefresh = false) => {
        toast.info('Generating AI semantics... This may take 30-60 seconds.')

        updateSemantics({
            inboxID: inbox.inboxID,
            inboxName: inbox.inboxName,
            forceRefresh: forceRefresh,
            onSuccess: (data) => {
                // Backend returns 200 but with error object
                if (data?.error) {
                    console.error('Update semantics error in response:', data)
                    toast.error(
                        data.error === 'Unable to process request'
                            ? "We're having problems generating AI semantics. Please try again later."
                            : data.error
                    )
                    return
                }

                // Success case
                invalidateInboxData()
                setSomeInputHasChanged({}) // Reset unsaved changes
                toast.success(data?.message || 'Dataset semantics updated successfully with AI!')
            },
            onError: (error) => {
                console.error('Update semantics error:', error)
                const errorMsg =
                    error?.errorMessage ||
                    error?.message ||
                    error?.error ||
                    "We're having problems generating AI semantics. Please try again later."
                toast.error(errorMsg)
            },
        })
    }

    // SPINNER
    if (isLoadingGetInboxData || isLoading) {
        return (
            <ScoopCenter className={styles.loadingContainer}>
                <ScoopSpinner size={80} />
            </ScoopCenter>
        )
    }

    return (
        <ScoopColumn className={styles.mainContainer}>
            <ScoopRow className={styles.headerSection}>
                <ScoopText className={styles.descriptionText}>
                    By default, we have inferred context around your overall dataset and for each of
                    the individual source fields, as well as any calculated fields generated. These
                    descriptions power our Al/ML features across the platform. You may edit them
                    freely.
                </ScoopText>
                <ScoopRow className={styles.buttonGroup}>
                    <ScoopButton
                        variant="grey"
                        onClick={handleRefreshSemanticsClick}
                        disabled={isLoadingGetInboxData || isUpdateSemanticsPending}
                        leftSection={
                            isUpdateSemanticsPending ? (
                                <CircleNotchIcon size={16} className="spinning" />
                            ) : (
                                <SparklesIcon size={16} />
                            )
                        }
                    >
                        {isUpdateSemanticsPending ? 'Generating...' : 'Refresh with AI'}
                    </ScoopButton>
                    <ScoopButton
                        onClick={handleConfirmSave}
                        disabled={
                            isLoadingGetInboxData ||
                            isUpdateSemanticsPending ||
                            !hasUnsavedChanges()
                        }
                    >
                        Save Changes
                    </ScoopButton>
                </ScoopRow>
            </ScoopRow>
            <ScoopColumn>
                <ScoopTextField
                    placeholder={'Example dataset description'}
                    multiline
                    rows={8}
                    label={'Dataset Semantics'}
                    value={datasetSemanticsData.semantics}
                    className={styles.datasetSemanticsInput}
                    disabled={isUpdateSemanticsPending}
                    onChange={(e) => {
                        const val = e.target.value
                        setDatasetSemanticsData({ ...datasetSemanticsData, semantics: val })
                        if (val !== originalSemanticData.datasetSemantics.semantics) {
                            setSomeInputHasChanged({
                                ...someInputHasChanged,
                                datasetSemantics: true,
                            })
                        } else {
                            setSomeInputHasChanged({
                                ...someInputHasChanged,
                                datasetSemantics: false,
                            })
                        }
                    }}
                />
            </ScoopColumn>

            <ScoopColumn className={styles.fieldsSection}>
                <ScoopRow className={styles.fieldsHeader}>
                    <ScoopText className={styles.fieldsTitle}>Fields</ScoopText>
                    <ScoopTextField
                        placeholder="Search fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchField}
                        disabled={isUpdateSemanticsPending}
                        leftSection={<MagnifyingGlassIcon size={16} />}
                    />
                </ScoopRow>
                <ScoopDivider />
                <ScoopGrid container className={styles.gridContainer}>
                    {datasetSemanticsData?.columnSemantics
                        ?.filter((semantic) =>
                            semantic?.columnName?.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        ?.sort((a, b) => a.columnName.localeCompare(b.columnName))
                        ?.map((semantic, index) => (
                            <ScoopGrid
                                item
                                xs={12}
                                key={`${index}-${semantic.columnName}`}
                                className={styles.gridItem}
                            >
                                <ScoopTextField
                                    placeholder={'Insert semantics description'}
                                    multiline
                                    rows={3}
                                    label={semantic.columnName}
                                    value={semantic.semantics}
                                    className={styles.fieldInput}
                                    disabled={isUpdateSemanticsPending}
                                    onChange={(e) => handleChangeInputs(e, semantic.columnName)}
                                />
                            </ScoopGrid>
                        ))}
                </ScoopGrid>
            </ScoopColumn>
        </ScoopColumn>
    )
}
